/**
 * Meta `_fbp` / `_fbc` per Conversions API docs:
 * https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 *
 * fbc = fb.{subdomainIndex}.{creationTimeMs}.{ACTUAL_FBCLID_VALUE}
 * fbp = fb.{subdomainIndex}.{creationTimeMs}.{randomNumber}
 *
 * Never append the literal string "fbclid" — that is the query param NAME, not the value.
 */

const FBP_COOKIE = '_fbp';
const FBC_COOKIE = '_fbc';
const COOKIE_MAX_AGE_DAYS = 90;

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    if (!match?.[1]) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string): void {
  try {
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function clearCookie(name: string): void {
  try {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // ignore
  }
}

/**
 * Meta subdomainIndex (‘com’=0, ‘example.com’=1, ‘www.example.com’=2).
 * Do not strip `www` — docs count it as a label.
 */
function subdomainIndex(): number {
  try {
    const parts = window.location.hostname.toLowerCase().split('.').filter(Boolean);
    return Math.max(0, parts.length - 1);
  } catch {
    return 1;
  }
}

/**
 * Real ClickID value from the URL (case-sensitive).
 * Rejects empty / placeholder values like the literal "fbclid".
 */
function isUsableFbclid(value: string | null | undefined): value is string {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;
  // Param name mistaken for value, or docs placeholder
  if (/^fbclid$/i.test(v)) return false;
  // Real Meta click ids are long (e.g. IwAR…)
  if (v.length < 10) return false;
  return true;
}

function readFbclidFromUrl(): string | null {
  try {
    // 1) query string
    const fromSearch = new URLSearchParams(window.location.search).get('fbclid');
    if (isUsableFbclid(fromSearch)) return fromSearch;

    // 2) hash query (SPA / theme quirks): #/?fbclid=… or #fbclid=…
    const hash = window.location.hash || '';
    const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash.replace(/^#/, '');
    const fromHash = new URLSearchParams(hashQuery).get('fbclid');
    if (isUsableFbclid(fromHash)) return fromHash;

    // 3) full href fallback — capture VALUE after fbclid=
    const hrefMatch = window.location.href.match(/[?&#]fbclid=([^&#]+)/);
    if (hrefMatch?.[1]) {
      const decoded = decodeURIComponent(hrefMatch[1]);
      if (isUsableFbclid(decoded)) return decoded;
    }
  } catch {
    // ignore
  }
  return null;
}

function fbclidFromFbc(fbc: string): string | null {
  const parts = fbc.split('.');
  if (parts.length < 4) return null;
  return parts.slice(3).join('.') || null;
}

function buildFbc(fbclid: string): string {
  // fbclid must be the ClickID value, never the string "fbclid"
  return `fb.${subdomainIndex()}.${Date.now()}.${fbclid}`;
}

function buildFbp(): string {
  const random = String(Math.floor(Math.random() * 2_147_483_647));
  return `fb.${subdomainIndex()}.${Date.now()}.${random}`;
}

function isValidFbc(value: string | null | undefined): value is string {
  if (!value) return false;
  if (!/^fb\.\d+\.\d+\..+/.test(value)) return false;
  const clickId = fbclidFromFbc(value);
  // Reject cookies that ended up as fb.1.<ts>.fbclid (param name, not value)
  return isUsableFbclid(clickId);
}

function isValidFbp(value: string | null | undefined): value is string {
  if (!value) return false;
  return /^fb\.\d+\.\d+\.\d+$/.test(value);
}

/**
 * Ensure `_fbp` / `_fbc` cookies exist per Meta guidance, then return values for CAPI.
 */
export function ensureMetaClickIds(): { fbp: string | null; fbc: string | null } {
  let fbp = readCookie(FBP_COOKIE);
  let fbc = readCookie(FBC_COOKIE);

  // Drop broken fbc from earlier bug (…ends with .fbclid)
  if (fbc && !isValidFbc(fbc)) {
    clearCookie(FBC_COOKIE);
    fbc = null;
  }

  if (!isValidFbp(fbp)) {
    fbp = buildFbp();
    writeCookie(FBP_COOKIE, fbp);
  }

  const fbclid = readFbclidFromUrl();
  if (fbclid) {
    const existingFbclid = fbc ? fbclidFromFbc(fbc) : null;
    if (!isValidFbc(fbc) || existingFbclid !== fbclid) {
      fbc = buildFbc(fbclid);
      writeCookie(FBC_COOKIE, fbc);
    }
  }

  return {
    fbp: isValidFbp(fbp) ? fbp : null,
    fbc: isValidFbc(fbc) ? fbc : null,
  };
}

export function getMetaClickIds(): { fbp: string | null; fbc: string | null } {
  return ensureMetaClickIds();
}

export const META_FBC_ATTR = '_intan_fbc';
export const META_FBP_ATTR = '_intan_fbp';

export async function syncMetaClickIdsToCart(): Promise<void> {
  const { fbp, fbc } = getMetaClickIds();
  if (!fbp && !fbc) return;

  try {
    const attributes: Record<string, string> = {};
    if (fbc) attributes[META_FBC_ATTR] = fbc;
    if (fbp) attributes[META_FBP_ATTR] = fbp;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
    });
  } catch {
    // non-fatal
  }
}
